/**
 * A/B experiments on the design of the RFS OOF modal (#rfs_unavailable_modal):
 *   rfs_oof_fwa_modal_design_2026_01, rfs_oof_fiber_modal_design_2026_01, rfs_oof_mobile_modal_design_2026_01
 * (server side: App\Experiments\RfsOofModal\RfsOofModalExperiments, docs/experiments/README.md).
 *
 * The OFFER ('fiber' | 'fwa' | 'pausal') is decided only by getRfsO2Offer() in app.js; this module never classifies an
 * address. It only
 *   1. asks the server for the variant of the ONE experiment of that offer (no exposure),
 *   2. switches the modal to variant B markup (rendered by experiments/rfs-oof-modal/b.blade.php for everybody),
 *   3. reports the exposure from the real modal OPEN, and
 *   4. hands the modal CTA click over to the destination page (referral_url + modal_type), see below.
 * Every failure falls back to the production modal (variant null). Never throws.
 *
 * Referral hand-over (first-party, one shot): a CTA click stores {u: referral_url, t: modal_type, ts} in the short-lived
 * cookie `upc_oof_ref` (path /, SameSite=Lax, 10 min). The first page load afterwards reads and ALWAYS deletes it;
 * only if it is valid for THAT page (same-origin URL, one of six modal types, destination slug matches, fresh) it emits
 *   dataLayer.push({ event: 'o2_oof_referral', referral_url: '<origin><path>', modal_type: 'o2_fwa_b' })
 * once. A refresh, a direct visit or a stale cookie therefore never emits anything.
 *
 * Temporary: remove with the experiments' cleanup (keep the referral part if it is approved as permanent analytics).
 */

const ENDPOINT = '/rfs-oof-modal/';
const REQUEST_TIMEOUT_MS = 1500;

const OFFERS = ['fiber', 'fwa', 'pausal'];

/** modal_type prefix and destination page slug per offer (mirror of RfsOofModalExperiments::OFFERS). */
const MODAL_TYPES = { fiber: 'o2_fiber', fwa: 'o2_fwa', pausal: 'o2_mobile' };
const DESTINATION_SLUGS = { fiber: 'o2-optika', fwa: 'o2-vzduchom', pausal: 'pausaly' };

export const REFERRAL_COOKIE = 'upc_oof_ref';
const REFERRAL_TTL_SECONDS = 600;
const REFERRAL_URL_MAX = 300;

const NO_VARIANT = Object.freeze({ variant: null, preview: false, modal_type: null });

function csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');

    return meta ? meta.getAttribute('content') || '' : '';
}

/** ?ab_preview=<one of the three experiment keys>:<A|B> of the page URL, else ''. */
export function previewParam(search = window.location.search) {
    try {
        const raw = new URLSearchParams(search).get('ab_preview') || '';

        return /^rfs_oof_(?:fwa|fiber|mobile)_modal_design_2026_01:[ABab]$/.test(raw) ? raw : '';
    } catch (e) {
        return '';
    }
}

/** POST JSON-less form request with a hard timeout. Resolves the parsed JSON, or null on ANY problem. */
function post(path, fields, preview = '') {
    try {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = controller ? window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
        const body = new URLSearchParams(fields);

        return fetch(ENDPOINT + path + (preview ? '?ab_preview=' + encodeURIComponent(preview) : ''), {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'X-CSRF-TOKEN': csrfToken(), 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
            body,
            signal: controller ? controller.signal : undefined,
            keepalive: true,
        })
            .then((response) => (response.ok && response.status !== 204 ? response.json() : null))
            .catch(() => null)
            .finally(() => {
                if (timer !== null) {
                    window.clearTimeout(timer);
                }
            });
    } catch (e) {
        return Promise.resolve(null);
    }
}

/**
 * Variant of the experiment of $offer for the modal that is about to open. Resolves {variant: 'A'|'B'|null, preview,
 * modal_type}; null = the experiment has no effect (not running, excluded IP, request failed): production modal.
 */
export function resolveVariant(offer) {
    if (!OFFERS.includes(offer)) {
        return Promise.resolve(NO_VARIANT);
    }

    return post('variant', { offer }, previewParam()).then((result) => {
        if (!result || (result.variant !== 'A' && result.variant !== 'B')) {
            return NO_VARIANT;
        }

        return {
            variant: result.variant,
            preview: result.preview === true,
            modal_type: typeof result.modal_type === 'string' ? result.modal_type : null,
        };
    });
}

/** Exposure of the experiment of the modal that has really opened. Fire and forget. */
export function exposeVariant(offer) {
    if (OFFERS.includes(offer)) {
        post('expose', { offer }, previewParam());
    }
}

/** Authorized admin preview of this page ({offer, variant}) or null; asks the server only when ?ab_preview= matches. */
export function fetchPreview() {
    const param = previewParam();

    if (param === '') {
        return Promise.resolve(null);
    }

    return post('preview', {}, param).then((result) =>
        result && result.preview === true && OFFERS.includes(result.offer) && (result.variant === 'A' || result.variant === 'B')
            ? { offer: result.offer, variant: result.variant }
            : null);
}

/** Switches the modal element to the given variant. Idempotent; the offer attribute is set for A and B alike. */
export function applyVariant(modal, offer, info) {
    if (!modal) {
        return;
    }

    const variant = info && info.variant ? info.variant : null;

    modal.setAttribute('data-rfs-oof-offer', OFFERS.includes(offer) ? offer : '');
    modal.setAttribute('data-rfs-oof-variant', variant || '');
    modal.setAttribute('data-rfs-oof-modal-type', info && info.modal_type ? info.modal_type : '');
    modal.classList.toggle('rfs-oof-variant-b', variant === 'B');

    if (variant === 'B') {
        // The B icons are fetched only now: every page with the checker contains the (inert) B markup.
        modal.querySelectorAll('img[data-src]').forEach((image) => {
            image.setAttribute('src', image.getAttribute('data-src'));
            image.removeAttribute('data-src');
        });
    }
}

// ---------------------------------------------------------------- referral

function destinationSlug(pathname) {
    const segments = String(pathname || '').split('/').filter(Boolean);

    return segments.length ? segments[segments.length - 1].toLowerCase() : '';
}

/** origin + pathname of a same-origin http(s) URL (no query, no hash, no credentials); '' for anything else. */
export function sanitizeReferralUrl(value, base = window.location) {
    try {
        const url = new URL(String(value), base.href);

        if ((url.protocol !== 'http:' && url.protocol !== 'https:') || url.host !== base.host) {
            return '';
        }

        const clean = url.origin + url.pathname;

        return clean.length <= REFERRAL_URL_MAX ? clean : '';
    } catch (e) {
        return '';
    }
}

function modalTypeToOffer(modalType) {
    const match = /^(o2_(?:fiber|fwa|mobile))_[ab]$/.exec(String(modalType || ''));

    if (!match) {
        return null;
    }

    return OFFERS.find((offer) => MODAL_TYPES[offer] === match[1]) || null;
}

function readCookie(name) {
    const prefix = name + '=';

    for (const part of document.cookie.split(';')) {
        const cookie = part.trim();

        if (cookie.indexOf(prefix) === 0) {
            return cookie.substring(prefix.length);
        }
    }

    return null;
}

function deleteCookie(name) {
    document.cookie = name + '=; path=/; max-age=0; SameSite=Lax' + (window.location.protocol === 'https:' ? '; Secure' : '');
}

/** Stores the hand-over for the destination page. Nothing for a modal without a real variant (no experiment / preview). */
export function storeReferral(modalType, win = window) {
    try {
        deleteCookie(REFERRAL_COOKIE);

        if (modalTypeToOffer(modalType) === null) {
            return false;
        }

        const url = sanitizeReferralUrl(win.location.href, win.location);

        if (url === '') {
            return false;
        }

        const value = encodeURIComponent(JSON.stringify({ u: url, t: modalType, ts: Math.floor(Date.now() / 1000) }));

        document.cookie = REFERRAL_COOKIE + '=' + value + '; path=/; max-age=' + REFERRAL_TTL_SECONDS + '; SameSite=Lax'
            + (win.location.protocol === 'https:' ? '; Secure' : '');

        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Reads and deletes the hand-over cookie (always, on the first page load after the click). Returns the validated
 * {referral_url, modal_type} only when it belongs to THIS page, else null.
 */
export function consumeReferral(win = window) {
    let raw;

    try {
        raw = readCookie(REFERRAL_COOKIE);

        if (raw === null) {
            return null;
        }

        deleteCookie(REFERRAL_COOKIE);

        const parsed = JSON.parse(decodeURIComponent(raw));
        const offer = modalTypeToOffer(parsed && parsed.t);
        const age = Math.floor(Date.now() / 1000) - Number(parsed && parsed.ts);
        const referralUrl = sanitizeReferralUrl(parsed && parsed.u, win.location);

        if (offer === null || referralUrl === '' || !(age >= -60 && age <= REFERRAL_TTL_SECONDS)) {
            return null;
        }

        // Only the page the modal's CTA leads to may report it (a landing-page redirect hop never does).
        if (destinationSlug(win.location.pathname) !== DESTINATION_SLUGS[offer]) {
            return null;
        }

        return { referral_url: referralUrl, modal_type: parsed.t };
    } catch (e) {
        return null;
    }
}

/** Journey Analytics metadata of a validated referral (the collector re-derives and re-validates all of it). */
export function referralMetadata(referral) {
    const match = /^o2_(fwa|fiber|mobile)_([ab])$/.exec(referral.modal_type);

    return {
        referral_url: referral.referral_url,
        referral_type: 'modal',
        offer_type: match[1],
        modal_variant: match[2].toUpperCase(),
        modal_type: referral.modal_type,
        destination_page: DESTINATION_SLUGS[modalTypeToOffer(referral.modal_type)],
    };
}

/**
 * Emits the one-shot referral on the destination page (no-op for a direct visit / refresh / invalid cookie / preview).
 * The cookie is resolved ONCE; the same validated payload feeds the dataLayer and Journey Analytics (tracker may be
 * omitted / a no-op when analytics is off).
 */
export function emitReferral(win = window, tracker = null) {
    const referral = consumeReferral(win);

    if (referral === null) {
        return null;
    }

    try {
        win.dataLayer = win.dataLayer || [];
        win.dataLayer.push({ event: 'o2_oof_referral', referral_url: referral.referral_url, modal_type: referral.modal_type });
    } catch (e) {}

    try {
        if (tracker && typeof tracker.track === 'function') {
            tracker.track('o2_oof_referral', referralMetadata(referral));
        }
    } catch (e) {}

    return referral;
}

let ctaBound = false;

/** One delegated listener for the modal CTAs of A and B (production cards + B panels). Idempotent. */
export function bindReferralCta() {
    if (ctaBound) {
        return;
    }

    ctaBound = true;

    document.addEventListener('click', (event) => {
        const cta = event.target && event.target.closest ? event.target.closest('#rfs_unavailable_modal [data-rfs-oof-cta]') : null;

        if (!cta) {
            return;
        }

        const modal = cta.closest('#rfs_unavailable_modal');
        const offer = cta.getAttribute('data-rfs-oof-cta');

        // The modal state is the source of truth: only the visible offer of an experiment variant reports a referral.
        storeReferral(modal && modal.getAttribute('data-rfs-oof-offer') === offer ? modal.getAttribute('data-rfs-oof-modal-type') : '');
    }, true);
}
